import React, { useCallback, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { MasterPreset, getAllCategories } from "../utils/masterPresets";
import { getOutputMeterLevel } from "../utils/audioUtils";
import { getAllPresets, savePresetToStorage } from "../utils/presetStorage";
import { captureCurrentState, CurrentStateRefs } from "../utils/presetCapture";
import { formatPresetAsCode } from "../utils/presetFormatter";

interface MasterPresetSelectorProps {
  selectedPreset: string;
  onPresetChange: (presetId: string) => void;
  isProgressionCustomized?: boolean;
  disabled?: boolean;
  bpm?: number;
  onBpmChange?: (bpm: number) => void;
  // Refs for capturing current state
  stateRefs?: CurrentStateRefs;
  // Play button props
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  isSamplerLoaded?: boolean;
}

const MasterPresetSelector: React.FC<MasterPresetSelectorProps> = ({
  selectedPreset,
  onPresetChange,
  isProgressionCustomized = false,
  disabled = false,
  bpm = 120,
  onBpmChange,
  stateRefs,
  isPlaying = false,
  onTogglePlay,
  isSamplerLoaded = false,
}) => {
  const [allPresets, setAllPresets] = useState<{ [key: string]: MasterPreset }>(
    () => getAllPresets(),
  );
  const [meterLevels, setMeterLevels] = useState<[number, number]>([0, 0]);
  const [isClipping, setIsClipping] = useState(false);
  const clipHoldRef = useRef(0);

  // Reload presets when component mounts or when a save/delete happens
  const reloadPresets = useCallback(() => {
    setAllPresets(getAllPresets());
  }, []);

  useEffect(() => {
    reloadPresets();
  }, [reloadPresets]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const { levels, isClipping: meterClipping } = getOutputMeterLevel();
      setMeterLevels(levels);
      if (meterClipping) {
        clipHoldRef.current = Date.now();
      }
      const clipActive = Date.now() - clipHoldRef.current < 450;
      setIsClipping(clipActive);
    }, 80);

    return () => window.clearInterval(intervalId);
  }, []);

  const handlePresetChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const presetId = e.target.value;
      onPresetChange(presetId);
    },
    [onPresetChange],
  );

  const handleBpmChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newBpm = parseInt(e.target.value, 10);
      onBpmChange?.(newBpm);
    },
    [onBpmChange],
  );

  // Handle save preset
  const handleSavePreset = useCallback(() => {
    if (!stateRefs) {
      alert("State refs not available. Cannot save preset.");
      return;
    }

    const name = prompt("Enter a name for this preset:");
    if (!name) return;

    const description = prompt("Enter a description (optional):") || "";

    try {
      const preset = captureCurrentState(name, description, stateRefs);
      savePresetToStorage(preset);
      reloadPresets();
      alert(`Preset "${name}" saved successfully!`);
      // Select the newly saved preset
      onPresetChange(preset.id);
    } catch (error) {
      console.error("Error saving preset:", error);
      alert("Failed to save preset. Check console for details.");
    }
  }, [stateRefs, reloadPresets, onPresetChange]);

  // Handle copy JSON
  const handleCopyJSON = useCallback(() => {
    if (!selectedPreset) {
      alert("No preset selected.");
      return;
    }

    const preset = allPresets[selectedPreset];
    if (!preset) {
      alert("Selected preset not found.");
      return;
    }

    try {
      const presetForExport = stateRefs
        ? {
            ...preset,
            chord: captureCurrentState(
              preset.name,
              preset.description,
              stateRefs,
            ).chord,
          }
        : preset;
      const jsonCode = formatPresetAsCode(presetForExport);
      navigator.clipboard.writeText(jsonCode);
      alert("Preset JSON copied to clipboard!");
    } catch (error) {
      console.error("Error copying JSON:", error);
      alert("Failed to copy JSON. Check console for details.");
    }
  }, [selectedPreset, allPresets, stateRefs]);

  // Group presets by category for better organization
  const categories = [...getAllCategories(), "custom"] as Array<
    MasterPreset["category"]
  >;
  const groupedPresets = categories.reduce(
    (acc, category) => {
      acc[category] = Object.values(allPresets).filter(
        (preset) => preset.category === category,
      );
      return acc;
    },
    {} as Record<string, MasterPreset[]>,
  );

  const meterRows = 2;
  const meterColumns = 14;
  const clipStartIndex = meterColumns - 2;
  const playButtonTitle = !isSamplerLoaded
    ? "Loading samples..."
    : isPlaying
      ? "Stop"
      : "Play";

  // Handle preset navigation
  const handlePreviousPreset = useCallback(() => {
    if (disabled) return;
    const presetIds = Object.keys(allPresets);
    const currentIndex = presetIds.indexOf(selectedPreset);
    if (currentIndex > 0) {
      onPresetChange(presetIds[currentIndex - 1]);
    } else if (presetIds.length > 0) {
      onPresetChange(presetIds[presetIds.length - 1]);
    }
  }, [disabled, allPresets, selectedPreset, onPresetChange]);

  const handleNextPreset = useCallback(() => {
    if (disabled) return;
    const presetIds = Object.keys(allPresets);
    const currentIndex = presetIds.indexOf(selectedPreset);
    if (currentIndex < presetIds.length - 1) {
      onPresetChange(presetIds[currentIndex + 1]);
    } else if (presetIds.length > 0) {
      onPresetChange(presetIds[0]);
    }
  }, [disabled, allPresets, selectedPreset, onPresetChange]);

  return (
    <MasterPresetSelectorContainer id="master-preset-selector">
      <HeaderContent id="header-content">
        {/* Left: Preset Selector with arrows */}
        <PresetSelectorWrapper id="preset-selector-wrapper">
          <PresetSelector id="preset-selector">
            <PresetArrowButton
              id="preset-prev-button"
              onClick={handlePreviousPreset}
              disabled={disabled}
              title="Previous preset"
            >
              <svg
                width="4"
                height="6"
                viewBox="0 0 4 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: "scaleX(-1)" }}
              >
                <path
                  d="M4 2.57143L4 3.42857L3 3.42857L3 4.28571L2 4.28571L2 5.14286L1 5.14286L1 6L0 6L7.15493e-08 0L1 1.19249e-08L1 0.857143L2 0.857143L2 1.71429L3 1.71429L3 2.57143L4 2.57143Z"
                  fill="currentColor"
                />
              </svg>
            </PresetArrowButton>
            <PresetSelectStyled
              id="master-preset-select"
              value={selectedPreset}
              onChange={handlePresetChange}
              disabled={disabled}
            >
              <option value="">-- Select a Preset --</option>
              {categories.map((category) => {
                const presetsInCategory = groupedPresets[category];
                if (!presetsInCategory || presetsInCategory.length === 0)
                  return null;
                return (
                  <optgroup
                    key={category}
                    label={category.charAt(0).toUpperCase() + category.slice(1)}
                  >
                    {presetsInCategory.map((preset) => (
                      <option
                        key={preset.id}
                        value={preset.id}
                        title={preset.description}
                      >
                        {isProgressionCustomized && selectedPreset === preset.id
                          ? `${preset.name} *`
                          : preset.name}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </PresetSelectStyled>
            <PresetArrowButton
              id="preset-next-button"
              onClick={handleNextPreset}
              disabled={disabled}
              title="Next preset"
            >
              <svg
                width="4"
                height="6"
                viewBox="0 0 4 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 2.57143L4 3.42857L3 3.42857L3 4.28571L2 4.28571L2 5.14286L1 5.14286L1 6L0 6L7.15493e-08 0L1 1.19249e-08L1 0.857143L2 0.857143L2 1.71429L3 1.71429L3 2.57143L4 2.57143Z"
                  fill="currentColor"
                />
              </svg>
            </PresetArrowButton>
          </PresetSelector>
          <SaveButton
            id="save-button"
            onClick={handleSavePreset}
            disabled={disabled}
            title="Save current state as new preset"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 2C2 1.44772 2.44772 1 3 1H10.5858C10.851 1 11.1054 1.10536 11.2929 1.29289L13.7071 3.70711C13.8946 3.89464 14 4.149 14 4.41421V14C14 14.5523 13.5523 15 13 15H3C2.44772 15 2 14.5523 2 14V2Z"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M5 1V4H10V1"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
              />
              <path
                d="M5 8H11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M5 11H11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </SaveButton>
          <ExportPresetButton
            id="export-preset-button"
            onClick={handleCopyJSON}
            disabled={disabled || !selectedPreset}
            title="Export preset"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 2V10M8 10L5 7M8 10L11 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              <path
                d="M2 12V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </ExportPresetButton>
        </PresetSelectorWrapper>

        {/* Center: Play Button */}
        {onTogglePlay && (
          <PlayButtonWrapper id="play-button-wrapper">
            <PlayButton
              id="play-button"
              type="button"
              $isActive={isPlaying}
              onClick={onTogglePlay}
              disabled={disabled}
              title={playButtonTitle}
              aria-busy={!isSamplerLoaded}
            >
              {isPlaying ? (
                <StopButtonIcon
                  id="stop-button-icon"
                  viewBox="0 0 64 64"
                  aria-hidden="true"
                >
                  <circle cx="32" cy="32" r="31" fill="none" />
                  <rect
                    x="22"
                    y="22"
                    width="20"
                    height="20"
                    rx="2"
                    fill="none"
                  />
                </StopButtonIcon>
              ) : (
                <PlayButtonIcon
                  id="play-button-icon"
                  viewBox="0 0 64 64"
                  aria-hidden="true"
                >
                  <circle cx="32" cy="32" r="31" />
                  <path d="M46 30.2676C47.3333 31.0374 47.3333 32.9626 46 33.7324L25 45.8564C23.6667 46.6262 22 45.6636 22 44.124L22 19.876C22 18.3845 23.5641 17.4344 24.874 18.0762L25 18.1436L46 30.2676Z" />
                </PlayButtonIcon>
              )}
            </PlayButton>
          </PlayButtonWrapper>
        )}

        {/* Right: Tempo Control */}
        <HeaderRight id="header-right">
          <TempoControlWrapper id="tempo-control-wrapper">
            <TempoLabel id="tempo-label">Tempo: {bpm}bpm</TempoLabel>
            <TempoSlider
              type="range"
              id="bpm"
              name="bpm"
              min="60"
              max="200"
              value={bpm}
              onChange={handleBpmChange}
              disabled={disabled}
            />
          </TempoControlWrapper>
          <OutputMeterWrapper
            id="output-meter"
            role="meter"
            aria-label="Output level meter"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(
              Math.max(meterLevels[0], meterLevels[1]) * 100,
            )}
            title={isClipping ? "Output level (clipping)" : "Output level"}
            $isClipping={isClipping}
          >
            {Array.from({ length: meterRows }, (_, rowIndex) => (
              <OutputMeterRow
                id={`output-meter-row-${rowIndex}`}
                key={`meter-row-${rowIndex}`}
              >
                {Array.from({ length: meterColumns }, (_, colIndex) => {
                  const rowLevel = meterLevels[rowIndex] ?? 0;
                  const activeDots = Math.round(rowLevel * meterColumns);
                  const isActive = colIndex < activeDots;
                  const isClipDot = colIndex >= clipStartIndex;
                  return (
                    <OutputMeterDot
                      id={`output-meter-dot-${rowIndex}-${colIndex}`}
                      key={`meter-dot-${rowIndex}-${colIndex}`}
                      $active={isActive}
                      $clip={isClipping && isClipDot}
                    />
                  );
                })}
              </OutputMeterRow>
            ))}
          </OutputMeterWrapper>
        </HeaderRight>
        {!onTogglePlay && <HeaderSpacer id="header-center-spacer" />}
      </HeaderContent>
    </MasterPresetSelectorContainer>
  );
};

export default MasterPresetSelector;

// Styled Components
const MasterPresetSelectorContainer = styled.div`
  padding: 0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderContent = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  gap: 20px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-self: end;
  gap: 16px;
`;

const HeaderSpacer = styled.div`
  height: 1px;
`;

const PresetSelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
`;

const PresetSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: transparent;
  border: 2px solid #cfc9c9;
  border-radius: 6px;
  min-width: 200px;
`;

const PresetArrowButton = styled.button`
  background: none;
  border: none;
  color: #cfc9c9;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  transition: color 0.2s ease;
  flex-shrink: 0;

  svg {
    display: block;
    width: 8px;
    height: 10px;
  }

  &:hover:not(:disabled) {
    color: #fff;
  }

  &:disabled {
    color: #555;
    cursor: not-allowed;
  }
`;

const PresetSelectStyled = styled.select`
  flex: 1;
  padding: 0;
  background-color: transparent;
  border: none;
  font-size: 14px;
  color: #cfc9c9;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  text-align: center;
  outline: none;

  &:hover:not(:disabled) {
    color: #fff;
  }

  &:disabled {
    color: #555;
    cursor: not-allowed;
  }

  option {
    background-color: #2a2a2a;
    color: #cfc9c9;
  }

  optgroup {
    background-color: #1a1a1a;
    color: #cfc9c9;
  }
`;

const SaveButton = styled.button`
  background: none;
  border: none;
  color: #cfc9c9;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: #fff;
  }

  &:disabled {
    color: #555;
    cursor: not-allowed;
  }

  svg {
    display: block;
    width: 16px;
    height: 16px;
  }
`;

const ExportPresetButton = styled.button`
  background: none;
  border: none;
  color: #cfc9c9;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: #fff;
  }

  &:disabled {
    color: #555;
    cursor: not-allowed;
  }

  svg {
    display: block;
    width: 16px;
    height: 16px;
  }
`;

const PlayButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const PlayButton = styled.button<{ $isActive: boolean }>`
  padding: 8px;
  border-radius: 999px;
  border: none;
  background: transparent !important;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px) scale(1.02);
    background: transparent !important;
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.99);
    background: transparent !important;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: transparent !important;
  }
`;

const PlayButtonIcon = styled.svg`
  width: 48px;
  height: 48px;
  display: block;
  stroke: #ffffff;
  fill: none;
  stroke-width: 2;
`;

const StopButtonIcon = styled(PlayButtonIcon)`
  fill: none;
`;

const TempoControlWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  justify-self: end;
`;

const OutputMeterWrapper = styled.div<{ $isClipping: boolean }>`
  display: grid;
  gap: 4px;
`;

const OutputMeterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(14, 7px);
  gap: 4px;
`;

const OutputMeterDot = styled.span<{ $active: boolean; $clip: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${({ $active, $clip }) => {
    if ($clip) return "#ff5a5a";
    return $active ? "#4de785" : "#2a2a2a";
  }};
  box-shadow: ${({ $active, $clip }) => {
    if ($clip) return "0 0 6px rgba(255, 90, 90, 0.45)";
    if ($active) return "0 0 6px rgba(77, 231, 133, 0.4)";
    return "none";
  }};
`;

const TempoLabel = styled.div`
  color: #cfc9c9;
  font-size: 14px;
  white-space: nowrap;
`;

const TempoSlider = styled.input`
  width: 120px;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    background: #ffffff;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    background: #ffffff;
    border-radius: 50%;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;
  }

  &::-moz-range-thumb:hover {
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
